/* eslint-disable no-underscore-dangle,no-useless-constructor,no-console */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
// adopt
import { adopt } from 'react-adopt';
// graphQL
import gql from 'graphql-tag';
// global variables
import {
  adStatus,
  defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS,
} from 'config';
// our components
import { Query, Mutation } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import FlaggedAdList from 'components/FlaggedAd/List/List';
import {
  REVIEW_ADS_MUTATION,
  UNPUBLISH_ADS_MUTATION,
  UNREVIEW_ADS_MUTATION,
  PUBLISH_ADS_MUTATION,
} from 'components/Ad/List';
import qs, { getQueryString } from 'helpers/queryStrings';
import RouterContext from 'helpers/routerContext';
import { bulkUpdate } from 'helpers/updateMutationsHelper';
import AuthContext from 'helpers/authContext';
import { getDateFromDefaultOrInitialFilter } from 'helpers/dateHelper';

/**
 * Query to fetch flagged ads from api
 */
export const FLAGGED_ADS_QUERY = gql`
  query FlaggedAdsList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $flags: [AdFlag]
    $flagsCountGte: Int
    $flaggedOnLte: DateTime
    $flaggedOnGte: DateTime
    $sortOrder: OrderEnum
    $sortField: flaggedSortFields!
    $reviewed: Boolean
  ) {
    flaggedAds(
      first: $first
      after: $after
      last: $last
      before: $before
      flags: $flags
      flagsCountGte: $flagsCountGte
      flaggedOnLte: $flaggedOnLte
      flaggedOnGte: $flaggedOnGte
      sortBy: [{ order: $sortOrder, field: $sortField }]
      reviewed: $reviewed
    ) @connection(key: "flagged_ads") {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          title
          isAuto
          subcategory {
            id
            title
            parentCategory {
              id
              title
            }
          }
          user {
            id
            fullname
            email
            phone
          }
          readyImages {
            id
            urls {
              high
              thumb
            }
          }
          reviewed
          reviewedOn
          reviewedBy {
            id
            fullname
          }
          name
          email
          firstPhone
          firstPhonePrefix
          firstPhoneType
          secondPhone
          secondPhonePrefix
          secondPhoneType
          whatsapp
          status
          updateCount
          createdOn
          updatedOn
          ipInsert
          ipUpdate
          flagCount
          lastFlaggedOn
          offensive: flag(flagType: PROHIBIDO_OFENSIVO) {
            lastFlaggedOn
            total
          }
          spam: flag(flagType: REPETIDO_SPAM) {
            lastFlaggedOn
            total
          }
          classified: flag(flagType: MAL_CATEGORIZADO) {
            lastFlaggedOn
            total
          }
          scam: flag(flagType: ESTAFA) {
            lastFlaggedOn
            total
          }
          joke: flag(flagType: FALSO_BROMA) {
            lastFlaggedOn
            total
          }
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * Default filters in FlaggedAdList
 */
export const defaultFilters = {
  flags: undefined,
  flaggedOnLte: undefined,
  flaggedOnGte: undefined,
  flagsCountGte: undefined,
  reviewed: undefined,
};

/**
 * @class
 * @description FlaggedAd List Component
 * @augments Component
 */
class FlaggedAdListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { initialFilters } = props;
    const {
      appliedOrder,
      direction,
      cursor,
      numberOfRowsSelected,
      ...initFilters
    } = initialFilters;
    const defaultDateFilters = this.getDefaultDateFilters();
    const flaggedOnGte = getDateFromDefaultOrInitialFilter({
      field: 'flaggedOnGte',
      defaultDateFilters,
      initialFilters,
    });
    const flaggedOnLte = getDateFromDefaultOrInitialFilter({
      field: 'flaggedOnLte',
      defaultDateFilters,
      initialFilters,
    });
    this.state = {
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 0) || DEFAULT_NUMBER_OF_ROWS,
      appliedOrder: appliedOrder || {
        orderField: 'flags_count',
        orderAsc: false,
        orderColumn: 'flags',
      },
      filters: {
        ...defaultFilters,
        ...initFilters,
        flaggedOnGte,
        flaggedOnLte,
      },
      pagination: { direction: direction || null, cursor: cursor || undefined },
      itemProcessing: [],
      failedIds: [],
      showNotification: false,
    };
    this.Compose = adopt({
      mutateAdUnPublish: this.adUnPublish,
      mutateAdPublish: this.adPublish,
      mutateAdReview: this.adReview,
      mutateAdUnReview: this.adUnReview,
      queryFlaggedAds: this.listFlaggedAds,
    });
    this.fetchMoreFlaggedAds = () => {};
  }

  /**
   * @description
   * Calc default date filters.
   *
   * @returns {Object} - Return default date filters.
   */
  getDefaultDateFilters = () => ({
    flaggedOnGte: moment()
      .subtract(5, 'day')
      .startOf('day')
      .format(),
    flaggedOnLte: moment()
      .endOf('day')
      .format(),
  });

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  listFlaggedAds = ({ render }) => {
    const {
      pagination,
      numberOfRowsSelected,
      filters,
      appliedOrder: { orderField, orderAsc },
    } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };
    return (
      <Query
        query={FLAGGED_ADS_QUERY}
        variables={{
          ...paginationVariables,
          ...filters,
          sortField: orderField,
          sortOrder: orderAsc ? 'asc' : 'desc',
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMoreFlaggedAds = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Mutation publish callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  adPublish = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <Mutation
        mutation={PUBLISH_ADS_MUTATION}
        onCompleted={this.mutationCompleted('publishAd', itemProcessing)}
        update={(cache, { data: { publishAd } }) => {
          const data = {
            status: adStatus.PUBLISHED_BY_MODERATOR,
            __typename: 'AdminFlagAdType',
          };

          bulkUpdate(
            cache,
            'AdminFlagAdType',
            'Publish',
            'status',
            data,
            publishAd.successIds,
          );
        }}
      >
        {handleAdPublish => render({ handleAdPublish })}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation unpublish callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  adUnPublish = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <Mutation
        mutation={UNPUBLISH_ADS_MUTATION}
        onCompleted={this.mutationCompleted('unPublishAd', itemProcessing)}
        update={(cache, { data: { unPublishAd } }) => {
          const data = {
            status: adStatus.UNPUBLISHED_BY_MODERATOR,
            __typename: 'AdminFlagAdType',
          };

          bulkUpdate(
            cache,
            'AdminFlagAdType',
            'Unpublish',
            'status',
            data,
            unPublishAd.successIds,
          );
        }}
      >
        {handleAdUnPublish => render({ handleAdUnPublish })}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation review callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  adReview = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <AuthContext.Consumer>
        {({ id, fullname }) => (
          <Mutation
            mutation={REVIEW_ADS_MUTATION}
            onCompleted={this.mutationCompleted('reviewAd', itemProcessing)}
            update={(cache, { data: { reviewAd } }) => {
              const data = {
                reviewed: true,
                reviewedBy: { id, fullname, __typename: 'UserType' },
                reviewedOn: Date.now(),
                __typename: 'AdminFlagAdType',
              };

              const field = `${'reviewed ' +
                'reviewedBy {id fullname} ' +
                'reviewedOn '}`;

              bulkUpdate(
                cache,
                'AdminFlagAdType',
                'Review',
                field,
                data,
                reviewAd.successIds,
              );
            }}
          >
            {handleAdReview => render({ handleAdReview })}
          </Mutation>
        )}
      </AuthContext.Consumer>
    );
  };

  /**
   * @description
   * Mutation unreview callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  adUnReview = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <AuthContext.Consumer>
        {() => (
          <Mutation
            mutation={UNREVIEW_ADS_MUTATION}
            onCompleted={this.mutationCompleted('unReviewAd', itemProcessing)}
            update={(cache, { data: { unReviewAd } }) => {
              const data = {
                reviewed: false,
                reviewedBy: null,
                reviewedOn: null,
                __typename: 'AdminFlagAdType',
              };

              const field = `${'reviewed ' +
                'reviewedBy {id fullname}' +
                'reviewedOn '}`;

              bulkUpdate(
                cache,
                'AdminFlagAdType',
                'Unreview',
                field,
                data,
                unReviewAd.successIds,
              );
            }}
          >
            {handleAdUnReview => render({ handleAdUnReview })}
          </Mutation>
        )}
      </AuthContext.Consumer>
    );
  };

  /**
   * @description
   * OnComplete delete mutation.
   *
   * @param {Object} field - Target field of mutation.
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @returns {undefined} - No returns value.
   */
  mutationCompleted = (field, itemProcessing) => async data => {
    const failedIds = itemProcessing.filter(
      item => !data[field].successIds.includes(item),
    );
    this.setState({
      itemProcessing: [],
      failedIds,
      showNotification: failedIds.length > 0,
    });
  };

  /**
   * @description
   * Query helpful function to update query's result.
   *
   * @param {Object} previousResult - Latest Query result.
   * @param {Object} options - UpdateQuery options.
   * @returns {Object} New or Previous query's result.
   */
  updateQuery = (previousResult, options) => {
    const { pageInfo, edges: newEdges } = options.fetchMoreResult.flaggedAds;
    if (newEdges.length !== 0) {
      return {
        flaggedAds: {
          __typename: previousResult.flaggedAds.__typename,
          edges: [...previousResult.flaggedAds.edges, ...newEdges],
          pageInfo: {
            ...previousResult.flaggedAds.pageInfo,
            hasNextPage: pageInfo.hasNextPage,
          },
        },
      };
    }
    return previousResult;
  };

  /**
   * @description
   * Handle change order.
   *
   * @param {Object} newOrder - Object that contain order mapping.
   */
  handleChangeOrder = newOrder => {
    const history = this.context;
    const {
      appliedOrder: { orderAsc, orderField },
    } = this.state;
    const appliedOrder = {
      orderField: newOrder.val,
      orderAsc: newOrder.val === orderField && !orderAsc,
      orderColumn: newOrder.column,
    };
    this.setState({ appliedOrder });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({ ...parsed, appliedOrder });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    const { flaggedOnGte, flaggedOnLte, ...rest } = filters;
    this.setState({
      filters: {
        ...defaultFilters,
        flaggedOnGte: flaggedOnGte === null ? undefined : flaggedOnGte,
        flaggedOnLte: flaggedOnLte === null ? undefined : flaggedOnLte,
        ...rest,
      },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { appliedOrder, numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...rest,
      flaggedOnGte: flaggedOnGte === null ? 'null' : flaggedOnGte,
      flaggedOnLte: flaggedOnLte === null ? 'null' : flaggedOnLte,
      appliedOrder,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change state to restart filters.
   */
  handleRestartFilters = () => {
    const history = this.context;
    const defaultDateFilters = this.getDefaultDateFilters();
    this.setState({
      filters: { ...defaultFilters, ...defaultDateFilters },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { appliedOrder, numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      appliedOrder,
      numberOfRowsSelected,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      flaggedOnGte: parsed.flaggedOnGte === null ? 'null' : parsed.flaggedOnGte,
      flaggedOnLte: parsed.flaggedOnLte === null ? 'null' : parsed.flaggedOnLte,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      flaggedOnGte: parsed.flaggedOnGte === null ? 'null' : parsed.flaggedOnGte,
      flaggedOnLte: parsed.flaggedOnLte === null ? 'null' : parsed.flaggedOnLte,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMoreFlaggedAds({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Close notification.
   * @returns {undefined} - No returns value.
   */
  handleCloseNotification = () => {
    this.setState({ showNotification: false });
  };

  /**
   * @description
   * Render ReviewQueueList component.
   *
   * @returns {undefined} - No returns.
   */
  render() {
    const {
      numberOfRowsSelected,
      pagination,
      filters,
      itemProcessing,
      failedIds,
      showNotification,
      appliedOrder,
    } = this.state;
    return (
      <this.Compose>
        {({
          mutateAdPublish: { handleAdPublish },
          mutateAdUnPublish: { handleAdUnPublish },
          mutateAdReview: { handleAdReview },
          mutateAdUnReview: { handleAdUnReview },
          queryFlaggedAds: { loading, data },
        }) => (
          <React.Fragment>
            <FlaggedAdList
              data={data}
              adPublish={items => {
                this.setState({
                  itemProcessing: [...itemProcessing, ...items],
                  failedIds: [],
                });
                handleAdPublish({ variables: { ids: items } });
              }}
              adUnPublish={items => {
                this.setState({
                  itemProcessing: [...itemProcessing, ...items],
                  failedIds: [],
                });
                handleAdUnPublish({ variables: { ids: items } });
              }}
              adReview={items => {
                this.setState({
                  itemProcessing: [...itemProcessing, ...items],
                  failedIds: [],
                });
                handleAdReview({ variables: { ids: items } });
              }}
              adUnReview={items => {
                this.setState({
                  itemProcessing: [...itemProcessing, ...items],
                  failedIds: [],
                });
                handleAdUnReview({ variables: { ids: items } });
              }}
              numberOfRowsSelected={numberOfRowsSelected}
              paginationDirection={pagination.direction}
              changePage={paginationVariables => {
                this.handleChangePage(paginationVariables);
              }}
              changeNumberOfRows={numberOfRows => {
                this.handleChangeNumberOfRows(numberOfRows);
              }}
              appliedFilter={filters}
              filter={this.handleFilter}
              refresh={this.handleRefresh}
              restartFilters={this.handleRestartFilters}
              appliedOrder={appliedOrder}
              changeOrder={this.handleChangeOrder}
              loading={Object.keys(data).length === 0}
              fullLoading={loading}
              itemProcessing={itemProcessing}
              failedIds={failedIds}
              showNotification={showNotification}
              closeNotification={this.handleCloseNotification}
            />
          </React.Fragment>
        )}
      </this.Compose>
    );
  }
}

/**
 * FlaggedAdListContainer PropTypes
 * @param initialFilters - prop initial Filters to apply
 */

FlaggedAdListContainer.propTypes = {
  initialFilters: PropTypes.shape({}).isRequired,
};

export default FlaggedAdListContainer;
